import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Calendar, MapPin, Users, BookOpen, ArrowRight, ChevronLeft, ChevronRight, CheckCircle, AlertCircle, Sparkles, Shield, Zap, RefreshCw, Clock3, MapPinned, BadgeEuro, Phone, Mail, LogIn, Send, UserPlus } from 'lucide-react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useSiteSettings } from '../hooks/useSiteSettings'
import ScrollReveal from '../components/ScrollReveal'
import Modal from '../components/Modal'
import { PUBLIC_CONTACT_EMAIL } from '../config/contact'

interface Post {
  id: number
  title: string
  content: string
  imageUrl: string | null
  createdAt: string
  expiresOn: string | null
  author: {
    firstName: string
    lastName: string
  }
  event?: {
    id: number
    title: string
    description: string
    date: string
    location?: string
    maxAttendees?: number
    isPublic: boolean
    imageUrl?: string
  }
}

interface Member {
  id: number;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  isActive: boolean;
  age: number | null;
}

interface Event {
  id: number;
  title: string;
  description: string;
  date: string;
  location?: string;
  maxAttendees?: number;
  isPublic: boolean;
  visibility: 'ALL' | 'PUBLIC' | 'MEMBERS';
  imageUrl?: string;
  attendeeCount?: number;
  isFull?: boolean;
  attendees?: Array<{
    user: {
      id: number;
      firstName: string;
      lastName: string;
      email: string;
    };
  }>;
}

type NewsItem = 
  | (Post & { _type: 'post', _date: string, _originalIndex: number })
  | (Event & { _type: 'event', _date: string });

interface RegistrationFormData {
  name: string
  email: string
  message: string
}

const formatEventDate = (date: string) => new Date(date).toLocaleDateString('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

const getApiErrorMessage = (error: unknown, fallback: string) => (
  axios.isAxiosError<{ message?: string }>(error)
    ? error.response?.data?.message || fallback
    : fallback
)

const RegistrationEventSummary = ({ event }: { event: Event }) => (
  <div className="registration-event-summary">
    <span className="registration-event-icon" aria-hidden="true"><Calendar /></span>
    <div>
      <p>{event.title}</p>
      <span><Calendar aria-hidden="true" /> {formatEventDate(event.date)}</span>
      {event.location && <span><MapPin aria-hidden="true" /> {event.location}</span>}
    </div>
  </div>
)

interface PublicRegistrationModalProps {
  event: Event | null
  onClose: () => void
  onRegister: (eventId: number, formData: RegistrationFormData) => Promise<boolean>
}

const PublicRegistrationModal = ({ event, onClose, onRegister }: PublicRegistrationModalProps) => {
  const [formData, setFormData] = useState<RegistrationFormData>({ name: '', email: '', message: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    setFormData({ name: '', email: '', message: '' })
    setIsSubmitting(false)
  }, [event?.id])

  const handleSubmit = async (submitEvent: React.FormEvent<HTMLFormElement>) => {
    submitEvent.preventDefault()
    if (!event || isSubmitting) return

    setIsSubmitting(true)
    const registered = await onRegister(event.id, formData)
    setIsSubmitting(false)
    if (registered) onClose()
  }

  return (
    <Modal
      isOpen={event !== null}
      onClose={isSubmitting ? () => undefined : onClose}
      title="Réserver votre place"
      eyebrow="Inscription publique"
      size="md"
      footer={event ? (
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={isSubmitting}>Annuler</button>
          <button type="submit" form="public-event-registration" className="btn-primary" disabled={isSubmitting}>
            <Send aria-hidden="true" className="h-4 w-4" />
            {isSubmitting ? 'Inscription en cours…' : "S'inscrire"}
          </button>
        </>
      ) : undefined}
    >
      {event && (
        <form id="public-event-registration" className="registration-form" onSubmit={handleSubmit}>
          <RegistrationEventSummary event={event} />

          <div className="registration-member-callout">
            <span aria-hidden="true"><LogIn /></span>
            <div>
              <strong>Vous êtes déjà adhérent ?</strong>
              <p>Connectez-vous pour vous inscrire avec votre compte club.</p>
            </div>
            <Link to="/login" className="btn-secondary" onClick={onClose}>Se connecter</Link>
          </div>

          <div className="registration-form-grid">
            <label htmlFor="public-registration-name">
              Nom complet <span aria-hidden="true">*</span>
              <input
                id="public-registration-name"
                type="text"
                autoComplete="name"
                required
                value={formData.name}
                onChange={(changeEvent) => setFormData((current) => ({ ...current, name: changeEvent.target.value }))}
              />
            </label>
            <label htmlFor="public-registration-email">
              Adresse email <span aria-hidden="true">*</span>
              <input
                id="public-registration-email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={(changeEvent) => setFormData((current) => ({ ...current, email: changeEvent.target.value }))}
              />
            </label>
          </div>

          <label className="registration-message-field" htmlFor="public-registration-message">
            Message <span>(optionnel)</span>
            <textarea
              id="public-registration-message"
              rows={3}
              value={formData.message}
              onChange={(changeEvent) => setFormData((current) => ({ ...current, message: changeEvent.target.value }))}
              placeholder="Une information utile pour l'équipe du club…"
            />
          </label>
          <p className="registration-privacy-note">
            Vos coordonnées servent uniquement à gérer cette inscription et à vous envoyer sa confirmation.
          </p>
        </form>
      )}
    </Modal>
  )
}

interface RegistrationConfirmationModalProps {
  event: Event | null
  isSubmitting: boolean
  onCancel: () => void
  onConfirm: () => void
}

const RegistrationConfirmationModal = ({ event, isSubmitting, onCancel, onConfirm }: RegistrationConfirmationModalProps) => (
  <Modal
    isOpen={event !== null}
    onClose={isSubmitting ? () => undefined : onCancel}
    title="Confirmer votre inscription"
    eyebrow="Dernière étape"
    size="sm"
    footer={event ? (
      <>
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={isSubmitting}>Annuler</button>
        <button type="button" className="btn-primary" onClick={onConfirm} disabled={isSubmitting}>
          <CheckCircle aria-hidden="true" className="h-4 w-4" />
          {isSubmitting ? 'Confirmation…' : 'Confirmer ma place'}
        </button>
      </>
    ) : undefined}
  >
    {event && (
      <div className="registration-confirmation">
        <span className="registration-confirmation-icon" aria-hidden="true"><UserPlus /></span>
        <p>Votre place sera réservée avec votre compte adhérent.</p>
        <RegistrationEventSummary event={event} />
      </div>
    )}
  </Modal>
)

const Home = () => {
  const { user } = useAuth()
  const { settings } = useSiteSettings()
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [birthdayMember, setBirthdayMember] = useState<Member[] | null>(null)
  const [events, setEvents] = useState<Event[]>([]);
  const [userRegistrations, setUserRegistrations] = useState<Set<number>>(new Set());
  
  // États pour les modals
  const [currentPostIndex, setCurrentPostIndex] = useState<number | null>(null);
  const [currentEventIndex, setCurrentEventIndex] = useState<number | null>(null);
  const [showEventRegistration, setShowEventRegistration] = useState<Event | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState<Event | null>(null);
  const [registrationBusy, setRegistrationBusy] = useState(false);

  // Ajoute un état pour l'index de départ du carousel
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [newsSlideDirection, setNewsSlideDirection] = useState<'idle' | 'next' | 'previous'>('idle');
  const newsRailRef = useRef<HTMLDivElement>(null);
  const newsRailItemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Ouvre le modal sur un post ou un événement
  const openPostModal = (idx: number) => {
    const item = newsItems[idx] as NewsItem;
    if (item._type === 'post') {
      setCurrentPostIndex(item._originalIndex);
      setCurrentEventIndex(null);
    } else {
      // Pour les événements, on ouvre le modal d'événement
      const eventIndex = events.findIndex(e => e.id === item.id);
      setCurrentEventIndex(eventIndex);
      setCurrentPostIndex(null);
    }
  };

  // Ferme le modal
  const closePostModal = () => {
    setCurrentPostIndex(null);
    setCurrentEventIndex(null);
  };

  // Gère l'inscription à un événement
  const handleEventRegistration = (event: Event, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    
    if (!user) setShowEventRegistration(event);
    else setShowConfirmationModal(event);
  };

  // Inscription directe à un événement
  const registerToEvent = async (eventId: number) => {
    setRegistrationBusy(true)
    try {
      await axios.post(`/api/events/${eventId}/register`);
      toast.success('Inscription réussie ! Un email de confirmation vous a été envoyé.');
      setShowEventRegistration(null);
      setShowConfirmationModal(null);
      // Mettre à jour l'état local
      setUserRegistrations(prev => new Set([...prev, eventId]));
      // Recharger les événements pour mettre à jour le compteur
      void fetchEvents();
    } catch (error: unknown) {
      console.error('Erreur lors de l\'inscription:', error);
      toast.error(getApiErrorMessage(error, 'Erreur lors de l\'inscription'));
    } finally {
      setRegistrationBusy(false)
    }
  };

  // Inscription publique à un événement
  const registerPublicToEvent = async (eventId: number, formData: RegistrationFormData) => {
    try {
      await axios.post(`/api/events/${eventId}/public-register`, formData);
      toast.success('Inscription réussie ! Un email de confirmation vous a été envoyé.');
      setShowEventRegistration(null);
      // Recharger les événements pour mettre à jour le compteur
      void fetchEvents();
      return true
    } catch (error: unknown) {
      console.error('Erreur lors de l\'inscription:', error);
      toast.error(getApiErrorMessage(error, 'Erreur lors de l\'inscription'));
      return false
    }
  };

  // Se désinscrire d'un événement
  const unregisterFromEvent = async (eventId: number) => {
    const reason = window.prompt('Motif du désistement (obligatoire, 1 000 caractères maximum) :')?.trim()
    if (!reason) return
    try {
      await axios.delete(`/api/events/${eventId}/register`, { data: { reason } });
      toast.success('Désinscription réussie !');
      setUserRegistrations(prev => {
        const newSet = new Set(prev);
        newSet.delete(eventId);
        return newSet;
      });
      // Recharger les événements pour mettre à jour le compteur
      void fetchEvents();
    } catch (error: unknown) {
      console.error('Erreur lors de la désinscription:', error);
      toast.error(getApiErrorMessage(error, 'Erreur lors de la désinscription'));
    }
  };

  // Fonction pour défiler à droite
  const handleCarouselNext = () => {
    setNewsSlideDirection('next');
    setCarouselIndex((index) => newsItems.length > 0 ? (index + 1) % newsItems.length : 0);
  };
  // Fonction pour défiler à gauche
  const handleCarouselPrev = () => {
    setNewsSlideDirection('previous');
    setCarouselIndex((index) => newsItems.length > 0 ? (index - 1 + newsItems.length) % newsItems.length : 0);
  };

  // Déclare fetchPosts et fetchEvents ici
  const fetchPosts = async () => {
    try {
      const response = await axios.get('/api/posts')
      setPosts(response.data)
    } catch (error) {
      console.error('Erreur lors du chargement des posts:', error)
    }
  }
  const fetchEvents = async () => {
    try {
      const response = await axios.get('/api/events');
      setEvents(response.data);
      
      // Si l'utilisateur est connecté, récupérer ses inscriptions
      if (user) {
        const userEventIds = new Set<number>();
        response.data.forEach((event: Event) => {
          if (event.attendees?.some((attendee) => attendee.user.id === user.id)) {
            userEventIds.add(event.id);
          }
        });
        setUserRegistrations(userEventIds);
      }
    } catch (error) {
      // ignore
    }
  }

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchPosts(), fetchEvents()]).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    const fetchMembers = async () => {
      try {
        const response = await axios.get('/api/users')
        const today = new Date();
        const todayMonth = today.getMonth() + 1
        const todayDate = today.getDate()
        const birthdays = response.data.filter((m: Member) => {
          if (!m.dateOfBirth || !m.isActive) return false
          const d = new Date(m.dateOfBirth)
          return d.getMonth() + 1 === todayMonth && d.getDate() === todayDate
        });
        setBirthdayMember(birthdays);
      } catch (error) {
        // ignore
      }
    }
    fetchMembers()
  }, [user])

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isBot = /bot|spider|crawl|preview|monitor|pingdom|facebookexternalhit|slackbot|discordbot|telegrambot/.test(userAgent);
    if (!isBot) {
      axios.post('/api/statistics/visit', {
        userAgent: navigator.userAgent,
        page: window.location.pathname
      }).catch(() => {});
    }
  }, []);

  // Les modales partagées gèrent Échap, le focus et le verrouillage du défilement.
  const selectedPost = currentPostIndex !== null ? posts[currentPostIndex] : null
  const postModal = (
    <Modal
      isOpen={selectedPost !== null}
      onClose={closePostModal}
      title={selectedPost?.title ?? 'Actualité du club'}
      eyebrow="Le journal du dojo"
      size="lg"
      footer={selectedPost && currentPostIndex !== null ? (
        <div className="home-post-footer">
          <p><span>{currentPostIndex + 1}</span> sur {posts.length} actualité{posts.length > 1 ? 's' : ''}</p>
          <div>
            <button
              type="button"
              className="modal-nav-button"
              onClick={() => setCurrentPostIndex((index) => index !== null ? Math.max(0, index - 1) : index)}
              disabled={currentPostIndex === 0}
              aria-label="Actualité précédente"
              title="Actualité précédente"
            >
              <ChevronLeft aria-hidden="true" />
            </button>
            <button type="button" className="btn-secondary" onClick={closePostModal}>Fermer</button>
            <button
              type="button"
              className="modal-nav-button"
              onClick={() => setCurrentPostIndex((index) => index !== null ? Math.min(posts.length - 1, index + 1) : index)}
              disabled={currentPostIndex === posts.length - 1}
              aria-label="Actualité suivante"
              title="Actualité suivante"
            >
              <ChevronRight aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : undefined}
    >
      {selectedPost && (
        <article className="home-post-modal">
          {selectedPost.imageUrl && <img src={selectedPost.imageUrl} alt="" className="mb-5 max-h-80 w-full rounded-2xl object-cover" />}
          <div className="home-post-intro">
            <span className="home-post-icon" aria-hidden="true"><BookOpen /></span>
            <div>
              <span>Actualité du club</span>
              <strong>
                Publiée le {new Date(selectedPost.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </strong>
              <small>Par {selectedPost.author.firstName} {selectedPost.author.lastName}</small>
            </div>
          </div>

          <p className="home-post-content">{selectedPost.content}</p>

          {selectedPost.event && (
            <section className="home-post-event">
              <div className="home-post-event-heading">
                <span aria-hidden="true"><Calendar /></span>
                <div>
                  <small>Événement associé</small>
                  <strong>{selectedPost.event.title}</strong>
                </div>
              </div>
              <p>{selectedPost.event.description}</p>
              <div className="home-post-event-meta">
                <span><Calendar aria-hidden="true" /> {formatEventDate(selectedPost.event.date)}</span>
                {selectedPost.event.location && <span><MapPin aria-hidden="true" /> {selectedPost.event.location}</span>}
                {selectedPost.event.isPublic && <span><Users aria-hidden="true" /> Inscriptions ouvertes au public</span>}
              </div>
            </section>
          )}
        </article>
      )}
    </Modal>
  )


  const selectedEvent = currentEventIndex !== null ? events[currentEventIndex] : null;
  const selectedEventIsRegistered = selectedEvent
    ? userRegistrations.has(selectedEvent.id)
    : false;
  const selectedEventCanRegister = selectedEvent ? (user ? selectedEvent.visibility !== 'PUBLIC' : selectedEvent.visibility !== 'MEMBERS') : false;

  const eventModal = (
    <Modal
      isOpen={selectedEvent !== null}
      onClose={closePostModal}
      title={selectedEvent?.title ?? 'Événement du club'}
      eyebrow="Rendez-vous au dojo"
      size="lg"
      footer={selectedEvent ? (
        <>
          <button type="button" className="btn-secondary" onClick={closePostModal}>Fermer</button>
          {selectedEventCanRegister && selectedEventIsRegistered ? (
            <button
              type="button"
              className="btn-danger"
              onClick={() => unregisterFromEvent(selectedEvent.id)}
            >
              Se désinscrire
            </button>
          ) : selectedEventCanRegister && !selectedEvent.isFull ? (
            <button
              type="button"
              className="btn-primary"
              onClick={(event) => {
                closePostModal();
                handleEventRegistration(selectedEvent, event);
              }}
            >
              S'inscrire à l'événement
            </button>
          ) : selectedEventCanRegister && selectedEvent.isFull ? (
            <button type="button" className="btn-secondary" disabled>Événement complet</button>
          ) : null}
        </>
      ) : undefined}
    >
      {selectedEvent && (
        <div className="home-event-modal">
          <div
            className={`home-event-visual ${selectedEvent.imageUrl ? 'home-event-visual-image' : ''}`}
            style={selectedEvent.imageUrl ? { backgroundImage: `url(${selectedEvent.imageUrl})` } : undefined}
          >
            <div className="home-event-visual-content">
              <span className="home-event-visual-icon" aria-hidden="true"><Calendar /></span>
              <span className={`home-event-state ${selectedEventIsRegistered ? 'home-event-state-registered' : selectedEvent.isFull ? 'home-event-state-full' : 'home-event-state-open'}`}>
                {selectedEventIsRegistered ? 'Vous êtes inscrit' : selectedEvent.isFull ? 'Complet' : 'Inscriptions ouvertes'}
              </span>
            </div>
          </div>

          <p className="home-event-description">{selectedEvent.description}</p>

          <div className="home-event-detail-grid">
            <div className="home-event-detail">
              <span><Calendar aria-hidden="true" /> Date</span>
              <strong>{new Date(selectedEvent.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</strong>
            </div>
            <div className="home-event-detail">
              <span><Clock3 aria-hidden="true" /> Horaire</span>
              <strong>{new Date(selectedEvent.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</strong>
            </div>
            {selectedEvent.location && (
              <div className="home-event-detail">
                <span><MapPin aria-hidden="true" /> Lieu</span>
                <strong>{selectedEvent.location}</strong>
              </div>
            )}
            <div className="home-event-detail">
              <span><Users aria-hidden="true" /> Participants</span>
              <strong>
                {selectedEvent.attendeeCount ?? 0} inscrit{(selectedEvent.attendeeCount ?? 0) > 1 ? 's' : ''}
                {selectedEvent.maxAttendees ? ` sur ${selectedEvent.maxAttendees}` : ''}
              </strong>
            </div>
          </div>

          <div className={`home-event-notice ${selectedEvent.isFull ? 'home-event-notice-full' : ''}`}>
            {selectedEvent.isFull ? <AlertCircle aria-hidden="true" /> : <CheckCircle aria-hidden="true" />}
            <div>
              <strong>{selectedEvent.isFull ? 'La capacité maximale est atteinte' : selectedEvent.visibility === 'ALL' ? 'Inscription accessible à tous' : selectedEvent.visibility === 'PUBLIC' ? 'Inscription réservée aux visiteurs' : 'Inscription réservée aux membres'}</strong>
              <p>
                {selectedEvent.isFull
                  ? 'Les nouvelles inscriptions sont temporairement fermées.'
                  : selectedEvent.visibility === 'ALL'
                    ? 'Adhérents et visiteurs peuvent réserver leur place depuis cette fenêtre.'
                    : selectedEvent.visibility === 'PUBLIC' ? 'Les visiteurs peuvent réserver leur place depuis cette fenêtre.' : 'Les membres connectés peuvent réserver leur place.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );

  const newsItems: NewsItem[] = [
    ...posts.map((p, index) => ({ ...p, _type: 'post' as const, _date: p.createdAt, _originalIndex: index })),
    ...events.map(e => ({ ...e, _type: 'event' as const, _date: e.date }))
  ].sort((a, b) => new Date(b._date).getTime() - new Date(a._date).getTime());

  const activeNewsIndex = newsItems.length > 0 ? carouselIndex % newsItems.length : 0;
  const activeNews = newsItems[activeNewsIndex];

  useEffect(() => {
    if (newsItems.length < 2) return
    const timer = window.setInterval(() => {
      setNewsSlideDirection('next')
      setCarouselIndex((index) => (index + 1) % newsItems.length)
    }, 7000)
    return () => window.clearInterval(timer)
  }, [newsItems.length])

  useEffect(() => {
    const rail = newsRailRef.current
    const activeItem = newsRailItemRefs.current[activeNewsIndex]
    if (!rail || !activeItem) return

    const targetLeft = activeItem.offsetLeft - (rail.clientWidth - activeItem.offsetWidth) / 2
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    rail.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    })
  }, [activeNewsIndex])

  useEffect(() => {
    const rail = newsRailRef.current
    if (!rail) return

    const handleWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return

      const maxScrollLeft = rail.scrollWidth - rail.clientWidth
      const canScroll = event.deltaY > 0
        ? rail.scrollLeft < maxScrollLeft - 2
        : rail.scrollLeft > 2

      if (!canScroll) return

      event.preventDefault()
      rail.scrollLeft += event.deltaY
    }

    rail.addEventListener('wheel', handleWheel, { passive: false })
    return () => rail.removeEventListener('wheel', handleWheel)
  }, [loading, newsItems.length])

  return (
    <div>
      <div className="space-y-16 lg:space-y-24">
        {/* Bandeau anniversaire */}
        {user && birthdayMember && birthdayMember.length > 0 && (
          <div className="w-full bg-yellow-100 border border-yellow-300 text-yellow-900 text-center py-3 rounded-lg flex items-center justify-center gap-2 text-lg font-semibold">
            🎉 Joyeux anniversaire à {birthdayMember.map((m, i) => `${m.firstName}${m.age ? ` (${m.age} ans)` : ''}`).join(', ')} ! 🎉
          </div>
        )}
        {/* Hero Section */}
        <section
          className="hero-modern"
          style={settings.heroBackgroundUrl ? { backgroundImage: `url(${settings.heroBackgroundUrl})` } : undefined}
        >
          <span className="hero-motion-dot hero-motion-dot-one" aria-hidden="true"></span>
          <span className="hero-motion-dot hero-motion-dot-two" aria-hidden="true"></span>
          <div className="hero-content">
            <div>
              <span className="hero-kicker"><Sparkles className="h-3.5 w-3.5" /> Taï-Jitsu Club Drômois · FFKDA</span>
              <h1 className="hero-title">Maîtriser le corps.<br /><em>Éveiller</em> l'esprit.</h1>
              <p className="hero-lead">
                Une self-défense complète, fluide et accessible, transmise dans une ambiance familiale dans nos deux dojos à Loriol-sur-Drôme.
              </p>
              <div className="hero-actions">
                {!user ? (
                  <>
                    <Link to="/contact" className="btn-primary">Essayer un cours <ArrowRight className="h-4 w-4" /></Link>
                    <Link to="/login" className="btn-secondary">Espace membre</Link>
                    <a href="#club-news" className="btn-secondary">Actualités et événements</a>
                  </>
                ) : (
                  <Link to="/techniques" className="btn-primary">Explorer les techniques <ArrowRight className="h-4 w-4" /></Link>
                )}
              </div>
            </div>
            <div className="hero-metrics" aria-label="Les atouts du club">
              <div><strong>Dès 11 ans</strong><span>Femmes et hommes</span></div>
              <div><strong>2 dojos</strong><span>À Loriol-sur-Drôme</span></div>
              <div><strong>150 €</strong><span>La saison</span></div>
            </div>
          </div>
          <span className="hero-scroll-cue">Découvrir</span>
        </section>

        <div className="motion-marquee" aria-hidden="true">
          <div className="motion-marquee-track">
            {[0, 1].map((group) => (
              <div className="motion-marquee-group" key={group}>
                <span>Percussions avec esquive</span><span>Clés et contrôles</span><span>Projections</span><span>Maîtrise de soi</span><span>Réponse proportionnée</span>
              </div>
            ))}
          </div>
        </div>

        <ScrollReveal>
          <section>
            <div className="mb-10 max-w-3xl">
              <p className="eyebrow">La technique du corps</p>
              <h2 className="text-4xl font-bold md:text-6xl">Une self-défense qui s'adapte à la réalité.</h2>
              <p className="mt-6 text-lg leading-8 text-gray-600">
                Introduit en France par Jim Alcheik et intégré à la Fédération Française de Karaté depuis 1977, le Taï-Jitsu mobilise toutes les armes naturelles du corps. L'objectif : répondre avec justesse, contrôle et proportion à une situation d'agression.
              </p>
            </div>
            <div className="discipline-grid">
              <article className="discipline-card"><span className="discipline-index"><Zap className="h-5 w-5" /></span><h3>Percuter & esquiver</h3><p>Déplacements, blocages et frappes se combinent pour sortir de l'axe et reprendre l'initiative sans dépendre de la force brute.</p></article>
              <article className="discipline-card"><span className="discipline-index"><RefreshCw className="h-5 w-5" /></span><h3>Projeter & déséquilibrer</h3><p>Le mouvement de l'attaque devient une opportunité pour guider, déséquilibrer et amener au sol de manière maîtrisée.</p></article>
              <article className="discipline-card"><span className="discipline-index"><Shield className="h-5 w-5" /></span><h3>Contrôler & protéger</h3><p>Clés, immobilisations et contrôles articulaires permettent de neutraliser tout en adaptant la réponse à la situation.</p></article>
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section className="club-story">
            <div className="club-story-copy">
              <p className="eyebrow !text-emerald-300">Un club à taille humaine</p>
              <h2 className="text-4xl font-bold md:text-5xl">Progresser sérieusement, pratiquer simplement.</h2>
              <p className="mt-6 max-w-2xl text-lg leading-8">
                Le Taï-Jitsu Club Drômois accueille femmes, hommes et adolescents à partir de 11 ans. Pierre Arnaud et Emmanuel Aubry, tous deux ceintures noires, transmettent une pratique exigeante dans un cadre familial, respectueux et accessible aux débutants.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {!user && <Link to="/contact" className="btn-primary">Réserver un cours d'essai <ArrowRight className="h-4 w-4" /></Link>}
                <a href="tel:+33610010588" className="btn-secondary"><Phone className="h-4 w-4" /> 06 10 01 05 88</a>
              </div>
            </div>
            <div className="club-story-facts">
              <div><strong>Club affilié FFKDA</strong><span>Une pratique encadrée et reconnue</span></div>
              <div><strong>Dès 11 ans</strong><span>Débutants et confirmés</span></div>
              <div><strong>2 ceintures noires</strong><span>Pierre Arnaud · Emmanuel Aubry</span></div>
              <div><strong>Cours d'essai gratuit</strong><span>Venez découvrir la discipline</span></div>
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section>
            <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
              <div><p className="eyebrow">Informations pratiques</p><h2 className="text-4xl font-bold md:text-5xl">Votre prochain entraînement commence ici.</h2></div>
              {!user && <Link to="/contact" className="btn-secondary"><Mail className="h-4 w-4" /> Écrire au club</Link>}
            </div>
            <div className="practice-grid">
              <article className="practice-card"><Clock3 className="h-7 w-7" /><strong>Deux cours par semaine</strong><p>Des séances d'1 h 30 pour apprendre, répéter et progresser à son rythme tout au long de la saison.</p></article>
              <article className="practice-card"><MapPinned className="h-7 w-7" /><strong>Deux dojos à Loriol</strong><p>Dojo Confluence et dojo du Gymnase des Écoles, tous deux à Loriol-sur-Drôme.</p></article>
              <article className="practice-card practice-card-highlight"><BadgeEuro className="h-7 w-7" /><strong>150 € la saison</strong><p>Licence FFKDA comprise, tarif dégressif pour les fratries et événements conviviaux du club.</p></article>
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section id="club-news" className="news-showcase scroll-mt-24">
            <div className="relative z-10 mb-8 flex flex-wrap items-end justify-between gap-4">
              <div><p className="eyebrow">Au rythme du dojo</p><h2 className="text-4xl font-bold md:text-6xl">La vie du club,<br />sans manquer un mouvement.</h2></div>
              <div className="flex items-center gap-3">
                {user?.isAdmin && <Link to="/admin" className="btn-secondary">Gérer les actualités</Link>}
                {newsItems.length > 1 && <div className="news-controls"><button className="news-control" onClick={handleCarouselPrev} aria-label="Actualité précédente"><ChevronLeft className="h-5 w-5" /></button><button className="news-control" onClick={handleCarouselNext} aria-label="Actualité suivante"><ChevronRight className="h-5 w-5" /></button></div>}
              </div>
            </div>

            {loading ? (
              <div className="relative z-10 flex justify-center py-24"><div className="h-9 w-9 animate-spin rounded-full border-2 border-white/20 border-b-emerald-300"></div></div>
            ) : activeNews ? (
              <div className="news-stage">
                <div className="news-featured-viewport">
                  <article
                    key={`${activeNews._type}-${activeNews.id}`}
                    className={`news-featured news-featured-${activeNews._type} ${newsSlideDirection === 'idle' ? '' : `news-featured-slide-${newsSlideDirection}`}`}
                    onClick={() => openPostModal(activeNewsIndex)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return
                      event.preventDefault()
                      openPostModal(activeNewsIndex)
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Ouvrir ${activeNews._type === 'event' ? "l'événement" : 'la publication'} ${activeNews.title}`}
                    style={activeNews.imageUrl ? { backgroundImage: `url(${activeNews.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
                  >
                    <div className="news-featured-type-icon" aria-hidden="true">
                      {activeNews._type === 'event' ? <Calendar className="h-8 w-8" /> : <BookOpen className="h-8 w-8" />}
                    </div>
                    <div className="news-featured-content">
                      <span className={`news-badge news-badge-${activeNews._type}`}>
                        {activeNews._type === 'event' ? <Calendar className="h-4 w-4" /> : <BookOpen className="h-4 w-4" />}
                        {activeNews._type === 'event' ? 'Événement à venir' : 'Actualité du club'}
                      </span>
                      <h3>{activeNews.title}</h3>
                      <p className="line-clamp-3">{activeNews._type === 'event' ? activeNews.description : activeNews.content}</p>
                      <div className="news-discover">Découvrir <ArrowRight className="h-4 w-4" /></div>
                      <div className="news-date">{new Date(activeNews._date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
                    </div>
                  </article>
                </div>
                {newsItems.length > 1 && (
                  <div
                    ref={newsRailRef}
                    className="news-rail"
                    aria-label="Toutes les actualités"
                  >
                    {newsItems.map((item, index) => (
                      <button
                        ref={(element) => { newsRailItemRefs.current[index] = element }}
                        key={`${item._type}-${item.id}`}
                        className={`news-rail-item news-rail-item-${item._type} ${index === activeNewsIndex ? 'news-rail-item-active' : ''}`}
                        aria-current={index === activeNewsIndex ? 'true' : undefined}
                        onClick={() => {
                          if (index === activeNewsIndex) return
                          setNewsSlideDirection(index > activeNewsIndex ? 'next' : 'previous')
                          setCarouselIndex(index)
                        }}
                      >
                        <small>
                          <span className="news-rail-kind">
                            {item._type === 'event' ? <Calendar className="h-3.5 w-3.5" /> : <BookOpen className="h-3.5 w-3.5" />}
                            {item._type === 'event' ? 'Événement' : 'Actualité'}
                          </span>
                          <span>{new Date(item._date).toLocaleDateString('fr-FR')}</span>
                        </small>
                        <strong className="line-clamp-2">{item.title}</strong>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="relative z-10 rounded-3xl border border-white/10 bg-white/5 py-20 text-center text-emerald-100/60"><BookOpen className="mx-auto mb-4 h-12 w-12" /><p>Les prochaines nouvelles du dojo arrivent bientôt.</p></div>
            )}
          </section>
        </ScrollReveal>
        <section>
          {postModal}
          {eventModal}
          <PublicRegistrationModal
            event={showEventRegistration}
            onClose={() => setShowEventRegistration(null)}
            onRegister={registerPublicToEvent}
          />
          <RegistrationConfirmationModal
            event={showConfirmationModal}
            isSubmitting={registrationBusy}
            onCancel={() => setShowConfirmationModal(null)}
            onConfirm={() => {
              if (showConfirmationModal) void registerToEvent(showConfirmationModal.id)
            }}
          />
        </section>

        {/* Call to action */}
        {!user && (
          <section className="card relative overflow-hidden py-14 text-center sm:py-20">
            <div className="pointer-events-none absolute -right-20 -top-32 h-80 w-80 rounded-full bg-emerald-300/20 blur-3xl"></div>
            <p className="eyebrow">Votre premier pas</p>
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-5xl">
              Prêt à monter sur le tatami ?
            </h2>
            <p className="mx-auto mb-8 max-w-2xl text-lg leading-8 text-gray-600">
              Une tenue de sport suffit pour votre première séance. Venez rencontrer les enseignants, découvrir le groupe et essayer gratuitement le Taï-Jitsu Drômois.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="btn-primary">
                Commencer maintenant
              </Link>
              <Link to="/login" className="btn-secondary">
                J'ai déjà un compte
              </Link>
            </div>
          </section>
        )}
      </div>
      <footer className="mt-12 py-6 border-t text-center text-gray-500 text-sm">
        Taï-Jitsu Club Drômois &copy; {new Date().getFullYear()} — <a href={`mailto:${PUBLIC_CONTACT_EMAIL}`} className="text-primary-600 underline">{PUBLIC_CONTACT_EMAIL}</a> · <a href="tel:+33610010588" className="text-primary-600 underline">06 10 01 05 88</a>
      </footer>
    </div>
  )
}

export default Home
